"use client";

/**
 * Orik's views of its own tools, in the harness tool renderer's keyed seat (`tool.call.toolview`).
 *
 * The harness renders every call; a tool with a view registered under its wire name gets that view, and the
 * rest get its generic card. These are the calls that read better as more than arguments and a result. They are
 * drawn with the harness's own primitives (`ui-primitives`, resolved through the module loader), so they look like
 * the tool rows beside them. Registration lives in `harness/orik-seats.ts`.
 */
import type { ComponentType, ReactNode } from "react";
import type { FileTypeIconProps } from "@deepseek-ai/dsh-client-ui-primitives";
import { DownloadIcon } from "lucide-react";
import { useSessionDeliveries } from "@/lib/agent/session-deliveries";
import { artifactFromFile } from "@/lib/artifacts";

/** The `ui-primitives` components these views draw with. */
export interface ToolViewPrimitives {
  readonly FileTypeIcon: ComponentType<FileTypeIconProps>;
}

/** One content block of a tool result, as far as these views read it. */
interface ContentBlock {
  readonly type: string;
  readonly text?: string;
}

/** The props the harness hands an atomic tool view, as far as these views read them. */
export type ToolViewProps = {
  /** The conversation, which the harness hands every session-scoped occupant. */
  readonly sessionId?: string;
  readonly callId: string;
  readonly toolName: string;
} & (
  | { readonly phase: "preparing"; readonly block: Record<string, unknown> }
  | { readonly phase: "start"; readonly block: { readonly argsRaw: string } }
  | { readonly phase: "result"; readonly block: { readonly call: { readonly argsRaw: string } | null; readonly content: readonly ContentBlock[]; readonly isError: boolean } }
);

/** A call's arguments, parsed; empty while they are still streaming or when they do not parse. */
function callArguments(props: ToolViewProps): Record<string, unknown> {
  const raw = props.phase === "start" ? props.block.argsRaw : props.phase === "result" ? props.block.call?.argsRaw : undefined;
  if (raw === undefined) return {};
  try {
    const value = JSON.parse(raw) as unknown;
    return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

/** A settled result's text. */
function resultText(props: ToolViewProps): string | undefined {
  if (props.phase !== "result") return undefined;
  return props.block.content.flatMap(block => block.type === "text" && typeof block.text === "string" ? [block.text] : []).join("\n");
}

/** The base name of a path. */
const baseName = (path: string): string => path.split("/").filter(Boolean).at(-1) ?? path;

/**
 * A `present` call: one row per presented file, which opens it in the right sidebar, with its download beside it.
 *
 * The harness's `present` declares each file by its path in the conversation's sandbox, and the right sidebar
 * previews it from there. Orik also keeps a durable copy of each file and records a delivery receipt naming the
 * call (`lib/agent/present-file.ts`): the download is that copy, so it keeps working after the sandbox is gone.
 */
export function presentView({ FileTypeIcon }: ToolViewPrimitives, openFile: (sessionId: string, path: string) => void): ComponentType<ToolViewProps> {
  return function PresentView(props: ToolViewProps): ReactNode {
    const deliveries = useSessionDeliveries(props.sessionId);
    const files = callArguments(props).files;
    const paths = Array.isArray(files) ? files.flatMap(file => typeof (file as { path?: unknown }).path === "string" ? [(file as { path: string }).path] : []) : [];
    const copies = deliveries
      .filter(delivery => delivery.callId === props.callId)
      .flatMap(delivery => delivery.files.flatMap(file => artifactFromFile({ data: file.url, mimeType: file.mediaType }) ?? []));
    const failed = props.phase === "result" && props.block.isError;
    if (failed || paths.length === 0) {
      return <p data-orik-ui="" className="text-[13px] leading-6 text-muted-foreground" data-error={failed || undefined}>
        {failed ? resultText(props) || "The file could not be presented." : "Presenting a file…"}
      </p>;
    }
    return <div data-orik-ui="" data-slot="presented-files" className="flex flex-wrap gap-2 py-1">
      {paths.map(path => {
        const name = baseName(path);
        const copy = copies.find(artifact => artifact.filename === name);
        return <div key={path} className="inline-flex max-w-full items-center gap-1 rounded-xl border border-border bg-card py-1.5 pr-1.5 pl-2.5">
          <button type="button" aria-label={`Open ${name}`} disabled={props.sessionId === undefined}
            onClick={() => { if (props.sessionId !== undefined) openFile(props.sessionId, path); }}
            className="flex min-w-0 items-center gap-2 rounded-md text-left text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring">
            <FileTypeIcon path={name} size={20} /><span className="truncate">{name}</span>
          </button>
          {copy !== undefined && <a href={copy.url} download={name} aria-label={`Download ${name}`}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
            <DownloadIcon className="size-4" aria-hidden />
          </a>}
        </div>;
      })}
    </div>;
  };
}
